import { createRootRoute, createRoute, createRouter, Navigate } from '@tanstack/react-router';
import { AppShell } from './components/AppShell';
import { AboutPage } from './features/about/AboutPage';
import { BookingsPage } from './features/bookings/BookingsPage';
import { DashboardPage } from './features/dashboard/DashboardPage';
import { ExpensesPage } from './features/expenses/ExpensesPage';
import { PublicPage } from './features/listing/PublicPage';
import { NotificationsPage } from './features/notifications/NotificationsPage';
import { PatientDetailPage } from './features/patients/PatientDetailPage';
import { PatientsPage } from './features/patients/PatientsPage';
import { NewReportPage } from './features/reports/NewReportPage';
import { ReportEditorPage } from './features/reports/ReportEditorPage';
import { ReportsPage } from './features/reports/ReportsPage';
import { SettingsPage } from './features/settings/SettingsPage';
import { TeamPage } from './features/team/TeamPage';
import { TemplateBuilderPage } from './features/templates/TemplateBuilderPage';
import { TemplatesPage } from './features/templates/TemplatesPage';
import { useWorkspace } from './lib/workspace';

const root = createRootRoute({ component: AppShell });

const index = createRoute({
  getParentRoute: () => root,
  path: '/',
  component: Landing,
});

/** The dashboard is the first page for people who can see it; everyone else starts at Reports. */
function Landing() {
  const ws = useWorkspace();
  return <Navigate to={ws.can('view_dashboard') ? '/dashboard' : '/reports'} replace />;
}
const dashboard = createRoute({
  getParentRoute: () => root,
  path: '/dashboard',
  component: DashboardPage,
});
const reports = createRoute({
  getParentRoute: () => root,
  path: '/reports',
  component: ReportsPage,
  validateSearch: (s: Record<string, unknown>): { q?: string } =>
    typeof s.q === 'string' && s.q ? { q: s.q } : {},
});
const newReport = createRoute({
  getParentRoute: () => root,
  path: '/reports/new',
  component: NewReportPage,
  validateSearch: (s: Record<string, unknown>): { patient?: string } =>
    typeof s.patient === 'string' && s.patient ? { patient: s.patient } : {},
});
const patients = createRoute({
  getParentRoute: () => root,
  path: '/patients',
  component: PatientsPage,
});
const patientDetail = createRoute({
  getParentRoute: () => root,
  path: '/patients/$patientId',
  component: PatientDetailPage,
});
const reportEditor = createRoute({
  getParentRoute: () => root,
  path: '/reports/$reportId',
  component: ReportEditorPage,
});
const templates = createRoute({
  getParentRoute: () => root,
  path: '/templates',
  component: TemplatesPage,
});
const template = createRoute({
  getParentRoute: () => root,
  path: '/templates/$templateId',
  component: TemplateBuilderPage,
});
const settings = createRoute({
  getParentRoute: () => root,
  path: '/settings',
  component: SettingsPage,
});
const about = createRoute({ getParentRoute: () => root, path: '/about', component: AboutPage });
const notifications = createRoute({
  getParentRoute: () => root,
  path: '/notifications',
  component: NotificationsPage,
});
const expenses = createRoute({
  getParentRoute: () => root,
  path: '/expenses',
  component: ExpensesPage,
});
const bookings = createRoute({
  getParentRoute: () => root,
  path: '/bookings',
  component: BookingsPage,
});
const publicPage = createRoute({
  getParentRoute: () => root,
  path: '/public-page',
  component: PublicPage,
});
const team = createRoute({ getParentRoute: () => root, path: '/team', component: TeamPage });

export const router = createRouter({
  routeTree: root.addChildren([
    index,
    dashboard,
    reports,
    newReport,
    patients,
    patientDetail,
    reportEditor,
    templates,
    template,
    settings,
    team,
    about,
    notifications,
    expenses,
    bookings,
    publicPage,
  ]),
  defaultPreload: 'intent',
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
